import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { ShoppingCart, ExternalLink, Check, AlertCircle, Loader2, ListChecks } from 'lucide-react';

const API_URL = typeof window !== 'undefined' ? window.location.origin : (process.env.REACT_APP_BACKEND_URL || '');

export default function SharedShoppingList() {
  const { token } = useParams();
  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchList = async () => {
      try {
        const res = await fetch(`${API_URL}/api/shopping/shared/${token}`);
        if (!res.ok) throw new Error(res.status === 404 ? 'This list does not exist or the link has expired.' : 'Failed to load list.');
        const data = await res.json();
        setList(data.list);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    fetchList();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 size={36} className="animate-spin text-purple-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center gap-4 text-white p-6">
        <AlertCircle size={48} className="text-red-400" />
        <h2 className="text-xl font-semibold">List not found</h2>
        <p className="text-gray-400 text-sm text-center max-w-sm">{error}</p>
      </div>
    );
  }

  const items = list?.items || [];
  const checked = items.filter(i => i.is_checked).length;
  const progress = items.length > 0 ? (checked / items.length) * 100 : 0;

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <div className="border-b border-white/10 bg-black/95 sticky top-0 z-10">
        <div className="max-w-lg mx-auto px-4 py-4 flex items-center gap-3">
          <ShoppingCart size={22} className="text-purple-400 flex-shrink-0" />
          <div className="min-w-0">
            <h1 className="font-bold text-lg truncate">{list.name}</h1>
            <p className="text-xs text-gray-500">Shared shopping list</p>
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6 space-y-5">
        {/* Summary card */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-400">{checked} / {items.length} items</span>
            <span className="text-gray-400">{Math.round(progress)}% done</span>
          </div>
          <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
          {list.estimated_total > 0 && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-400">Est. Total</span>
              <div className="flex items-center gap-2">
                <span className={`font-semibold ${list.budget && list.estimated_total > list.budget ? 'text-red-400' : 'text-white'}`}>
                  ${list.estimated_total.toFixed(2)}
                </span>
                {list.budget && (
                  <span className="text-gray-600 text-xs">/ ${list.budget} budget</span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Items list */}
        {items.length === 0 ? (
          <div className="text-center py-16 text-gray-500">
            <ListChecks size={40} className="mx-auto mb-3 opacity-40" />
            <p className="text-sm">This list is empty</p>
          </div>
        ) : (
          <div className="space-y-2">
            {items.map((item) => (
              <div
                key={item.id}
                className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${
                  item.is_checked
                    ? 'bg-white/3 border-white/5 opacity-60'
                    : 'bg-white/5 border-white/10'
                }`}
                data-testid={`shared-item-${item.id}`}
              >
                {/* Checkbox indicator (read-only) */}
                <div className={`flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  item.is_checked ? 'bg-purple-600 border-purple-600' : 'border-white/30'
                }`}>
                  {item.is_checked && <Check size={11} className="text-white" />}
                </div>

                {/* Name + notes */}
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium truncate ${item.is_checked ? 'line-through text-gray-500' : 'text-white'}`}>
                    {item.name}
                  </p>
                  {item.notes && (
                    <p className="text-xs text-gray-500 truncate mt-0.5">{item.notes}</p>
                  )}
                </div>

                {/* Qty & price */}
                <div className="text-right flex-shrink-0 text-xs text-gray-400 space-y-0.5">
                  {item.quantity > 1 && <div>×{item.quantity}</div>}
                  {item.price && (
                    <div className="text-white font-medium">
                      ${(item.price * (item.quantity || 1)).toFixed(2)}
                    </div>
                  )}
                </div>

                {/* Link */}
                {(item.product_url || item.url) && (
                  <a
                    href={item.product_url || item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-shrink-0 p-1.5 bg-white/10 hover:bg-purple-600 rounded-lg transition-all"
                    title="View product"
                  >
                    <ExternalLink size={13} />
                  </a>
                )}
              </div>
            ))}
          </div>
        )}

        <p className="text-center text-xs text-gray-700 pt-4">
          Shared via CREATRVERSE · View only
        </p>
      </div>
    </div>
  );
}
